"use client";

import { RefObject, useEffect } from "react";
import publishEventToSDK from "./publish-event-to-sdk";

/**
 * Publish a `layoutUpdated` event to native on every re-layout of the observed
 * host, so the SDK can resize the webview container to fit its content.
 *
 * The `content_visible` impression that used to share this observer now lives in
 * {@link useContentVisible} — it only rode the `ResizeObserver` as a "layout
 * changed → re-check visibility" tick and is otherwise an unrelated concern.
 */
const useResizePublisher = (
  componentName: string,
  ref: RefObject<HTMLDivElement | null>
) => {
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      publishEventToSDK({
        topic: "layoutUpdated",
        data: { component: componentName },
      });
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        resizeObserver.unobserve(ref.current);
      }
      resizeObserver.disconnect();
    };
  }, [ref]);
};

export default useResizePublisher;
